import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { useLanguage } from '../../../../contexts/LanguageContext';
import useDealerApprovalDetail from '../hooks/useDealerApprovalDetail';
import ApprovalSteps from './components/ApprovalSteps';
import ApprovalChanges from './components/ApprovalChanges';
import ApprovalHistory from './components/ApprovalHistory';
import ApprovalBlocks from './components/ApprovalBlocks';
import ActionDialog from './components/ActionDialog';
import { resolveApprovalStatusText, approvalStatusSeverity, resolveFlowText } from '../../../../utils/dealerApprovalEnums';

const DealerApprovalDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { th } = useLanguage();

    const { detail, isLoading, isError, canAct, isBusy, doApprove, doReject, doReturn } = useDealerApprovalDetail(id);
    const [dialog, setDialog] = useState({ visible: false, mode: null });

    const openDialog = (mode) => setDialog({ visible: true, mode });
    const closeDialog = () => setDialog({ visible: false, mode: null });

    const handleConfirm = async (text) => {
        let ok = false;
        if (dialog.mode === 'approve') ok = await doApprove(text);
        else if (dialog.mode === 'reject') ok = await doReject(text);
        else if (dialog.mode === 'return') ok = await doReturn(text);
        if (ok) closeDialog();
    };

    if (isLoading) return <div className="card">{th('common.loading')}</div>;
    if (isError || !detail) return <div className="card text-red-600">{th('common.error')}</div>;

    return (
        <div className="card">
            {/* PrimeReact Timeline, opposite içeriği olmasa da karşı tarafa flex:1 boşluk
                ayırır; bu boşluğu daraltıp işaretçi/çizgiyi sola (başlık altına) hizalıyoruz. */}
            <style>{`.da-timeline .p-timeline-event-opposite { flex: 0; padding: 0; }`}</style>
            <div className="flex flex-row align-items-center justify-content-between mb-3">
                <div className="flex align-items-center gap-3">
                    <Button icon="pi pi-arrow-left" text onClick={() => navigate('/management/dealer-approval')} />
                    <h4 className="m-0">{th('dealerApproval.detailTitle')}</h4>
                    <Tag value={resolveApprovalStatusText(th, detail.status)} severity={approvalStatusSeverity(detail.status)} />
                    <Tag value={resolveFlowText(th, detail.flowKey)} severity={detail.flowKey === 'DEALER_PAYMENT' ? 'warning' : 'info'} />
                    {detail.isNewRegistration && <Tag value={th('dealerApproval.isNewRegistration')} severity="success" icon="pi pi-star" />}
                </div>
                {canAct && (
                    <div className="flex align-items-center gap-2">
                        <Button label={th('dealerApproval.approve')} icon="pi pi-check" severity="success" onClick={() => openDialog('approve')} disabled={isBusy} />
                        <Button label={th('dealerApproval.reject')} icon="pi pi-times" severity="danger" onClick={() => openDialog('reject')} disabled={isBusy} />
                        <Button label={th('dealerApproval.return')} icon="pi pi-replay" severity="warning" onClick={() => openDialog('return')} disabled={isBusy} />
                    </div>
                )}
            </div>

            <div className="grid mb-2">
                <div className="col-12 md:col-4">
                    <b>{th('dealerApproval.column.dealerName')}:</b> {detail.dealerName}
                </div>
                <div className="col-12 md:col-4">
                    <b>SAP:</b> {detail.sapDealerId}
                </div>
                <div className="col-12 md:col-4">
                    <b>{th('dealerApproval.currentStep')}:</b> {detail.currentStepOrder}
                </div>
            </div>

            <Divider />

            <ApprovalBlocks blocks={detail.blocks || []} />

            <div className="grid">
                <div className="col-12 md:col-4">
                    <ApprovalSteps steps={detail.steps || []} currentStepOrder={detail.currentStepOrder} />
                </div>
                <div className="col-12 md:col-5">
                    <ApprovalChanges changes={detail.changes || []} isNewRegistration={detail.isNewRegistration} />
                </div>
                <div className="col-12 md:col-3">
                    <ApprovalHistory history={detail.history || []} />
                </div>
            </div>

            <ActionDialog visible={dialog.visible} mode={dialog.mode} onHide={closeDialog} onConfirm={handleConfirm} loading={isBusy} />
        </div>
    );
};

export default DealerApprovalDetailPage;
